import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress,
  FormControlLabel,
  Switch,
  FormGroup,
  Stack
} from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@fortawesome/fontawesome-free/css/all.min.css';  // Make sure to install this package
import { masterDataService } from '../services/masterData.service';
import createBladeIcon from './Install-base/markers/BladeIcon';

// Layer display labels
const LAYER_LABELS = {
  STORES: 'Show Stores',
  WAREHOUSES: 'Show Walmart Distribution Centers',
  SUPPLIERS: 'Show Hershey DCs'
};

// Layer configuration
const MAP_LAYERS = {
  STORES: {
    id: 'stores',
    name: 'Stores',
    icon: 'circle',
    color: '#2196f3',
    visible: true,
    fetchData: (activeOnly) => activeOnly ? masterDataService.getActiveStores() : masterDataService.getStores(),
    renderMarker: (data, map) => {
      if (data.latitude && data.longitude) {
        const marker = L.circleMarker([data.latitude, data.longitude], {
          radius: 6,
          fillColor: '#2196f3',
          color: '#2196f3',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.4
        }).addTo(map);

        marker.bindPopup(`
          <div style="min-width: 200px;">
            <strong>${data.store_name}</strong><br>
            <strong>ID:</strong> ${data.store_id}<br>
            <strong>Type:</strong> ${data.store_type}<br>
            <strong>Address:</strong> ${data.address}<br>
            <strong>Location:</strong> ${data.city}, ${data.state} ${data.zip_code}<br>
            <strong>Size:</strong> ${data.store_size.toLocaleString()} sq ft<br>
            <strong>Capacity:</strong> ${data.store_capacity.toLocaleString()} units
          </div>
        `);
        return marker;
      }
      return null;
    }
  },
  WAREHOUSES: {
    id: 'warehouses',
    name: 'Walmart Distribution Centers',
    icon: 'blade',
    color: '#f44336',
    visible: true,
    fetchData: async (activeOnly) => {
      const [warehouses, inventoryStats] = await Promise.all([
        activeOnly ? masterDataService.getActiveWarehouses() : masterDataService.getWarehouses(),
        masterDataService.getInventoryStatistics()
      ]);
      
      // Merge inventory data with warehouse data
      if (warehouses.status === 'success' && inventoryStats.status === 'success') {
        // Group inventory data by warehouse
        const warehouseInventory = new Map();
        inventoryStats.data.details.forEach(item => {
          if (!item.warehouse_id) return;
          
          if (!warehouseInventory.has(item.warehouse_id)) {
            warehouseInventory.set(item.warehouse_id, {
              total_skus: 0,
              in_stock_skus: 0,
              under_stock_skus: 0,
              no_stock_skus: 0,
              details: []
            });
          }
          
          const stats = warehouseInventory.get(item.warehouse_id);
          stats.total_skus++;
          stats.details.push(item);
          
          switch (item.stock_status) {
            case 'in_stock':
              stats.in_stock_skus++;
              break;
            case 'under_stock':
              stats.under_stock_skus++;
              break;
            case 'no_stock':
              stats.no_stock_skus++;
              break;
          }
        });
        
        return {
          ...warehouses,
          data: warehouses.data.map(warehouse => ({
            ...warehouse,
            inventory: warehouseInventory.get(warehouse.warehouse_id)
          }))
        };
      }
      return warehouses;
    },
    renderMarker: (data, map) => {
      if (data.location?.latitude && data.location?.longitude) {
        // Calculate stock ratio based on SKU counts
        let stockRatio = 100;
        let stockStatus = 'healthy';
        if (data.inventory) {
          const { total_skus, in_stock_skus } = data.inventory;
          if (total_skus > 0) {
            stockRatio = (in_stock_skus / total_skus) * 100;
            stockStatus = stockRatio < 90 ? 'critical' : stockRatio < 95 ? 'warning' : 'healthy';
          }
        }

        // Determine circle color based on stock status
        let circleColor;
        if (!data.is_active) {
          circleColor = "#9E9E9E"; // Gray for inactive
        } else {
          switch (stockStatus) {
            case 'critical':
              circleColor = "#f44336"; // Red for critical (< 90%)
              break;
            case 'warning':
              circleColor = "#ff9800"; // Orange for warning (< 95%)
              break;
            case 'healthy':
            default:
              circleColor = "#4CAF50"; // Green for healthy (>= 95%)
              break;
          }
        }

        const marker = L.circleMarker([data.location.latitude, data.location.longitude], {
          radius: 8,
          fillColor: circleColor,
          color: circleColor,
          weight: 2,
          opacity: 1,
          fillOpacity: 0.7
        }).addTo(map);

        // Debug: Log the location data
        console.log('Warehouse location data:', {
          warehouse_id: data.warehouse_id,
          locality: data.location.address.locality,
          region: data.location.address.region,
          full_address: data.location.address
        });

        // Create detailed tooltip content
        let tooltipContent = `
          <div style="min-width: 200px;">
            <strong>${data.warehouse_name}</strong><br>
            <strong>ID:</strong> ${data.warehouse_id}<br>
            <strong>Type:</strong> ${data.warehouse_type}<br>
            <strong>Address:</strong> ${data.location.address.line1}<br>
            <strong>Location:</strong> ${data.location.address.locality}, ${data.location.address.region}<br>
            <strong>Postal Code:</strong> ${data.location.address.postal_code}<br>
            <strong>Capacity:</strong><br>
            - Inbound Dock: ${data.capacity.inbound_dock}<br>
            - Outbound Dock: ${data.capacity.outbound_dock}<br>
            - Front of House: ${data.capacity.front_of_house}<br>
            - Back of House: ${data.capacity.back_of_house}<br>
            <br>
            <a href="#" onclick="window.navigateToWarehouseInventory('${data.warehouse_id}'); return false;" 
               style="color: #1976d2; text-decoration: underline; font-weight: 500;">
              📦 View Inventory
            </a>
        `;

        // Add inventory information if available
        if (data.inventory) {
          const { total_skus, in_stock_skus, under_stock_skus, no_stock_skus } = data.inventory;
          tooltipContent += `
            <br><br><strong>Inventory Status:</strong><br>
            - 4-wk Avg Velocity: 980 cases/day <br>
            - On-hand at DC: 2,700 cases <br>
            - In-transit to stores: 1,350 cases <br>
            - Weeks of Supply:  <span style="color: red;">0.39 weeks (≈ 2.7 days)</span> <br>
            - Buffer Inventory Target: 9,800 cases (1.4 weeks) <br>
            - Anticipation Inventory (next 4 weeks): 4,000 cases <br>
            - On-order from Hershey plant:  <span style="color: red;">0 cases</span> <br>
          `;
        }
//            - Total SKUs: ${total_skus}<br>
//- In Stock SKUs: ${in_stock_skus} (${((in_stock_skus/total_skus)*100).toFixed(1)}%)<br>
//- Under Stock SKUs: ${under_stock_skus} (${((under_stock_skus/total_skus)*100).toFixed(1)}%)<br>
//- No Stock SKUs: ${no_stock_skus} (${((no_stock_skus/total_skus)*100).toFixed(1)}%)<br>
//- Stock Ratio: ${stockRatio.toFixed(1)}%<br>
//- Status: ${stockStatus.toUpperCase()}
        tooltipContent += '</div>';
        marker.bindPopup(tooltipContent);
        return marker;
      }
      return null;
    }
  },
  SUPPLIERS: {
    id: 'suppliers',
    name: 'Hershey DCs',
    icon: 'warehouse',
    color: '#2196f3',
    visible: true,
    fetchData: () => masterDataService.getSuppliers(),
    renderMarker: (data, map) => {
      if (data.latitude && data.longitude) {
        const icon = L.divIcon({
          html: `<i class="fas fa-warehouse" style="
            color: #2196f3; 
            font-size: 24px;
            text-shadow: 2px 2px 2px rgba(0,0,0,0.2);
          "></i>`,
          className: 'supplier-marker',
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        });

        const marker = L.marker([data.latitude, data.longitude], {
          icon
        }).addTo(map);

        marker.bindPopup(`
          <div>
            <strong>Supplier:</strong> ${data.supplier_name}<br>
            <strong>ID:</strong> ${data.supplier_id}<br>
            <strong>Type:</strong> ${data.supplier_type}<br>
            <strong>Category:</strong> ${data.supplier_category}<br>
            <strong>Location:</strong> ${data.warehouse_city}, ${data.warehouse_state}<br>
            <strong>Coordinates:</strong> ${data.latitude.toFixed(4)}, ${data.longitude.toFixed(4)}<br>
            <strong>Discount Rate:</strong> ${(data.discount_rate * 100).toFixed(1)}%
          </div>
        `);
        return marker;
      }
      return null;
    }
  }
};

const NetworkMap = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [layers, setLayers] = useState({
    stores: { ...MAP_LAYERS.STORES, visible: true },
    warehouses: { ...MAP_LAYERS.WAREHOUSES, visible: true },
    suppliers: { ...MAP_LAYERS.SUPPLIERS, visible: true }
  });
  const [layerData, setLayerData] = useState({});
  const linesLayerRef = useRef(null);
  const [mapInitialized, setMapInitialized] = useState(false);

  const initializeMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;

    mapInstanceRef.current = L.map(mapRef.current).setView([39.5, -98.35], 4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(mapInstanceRef.current);

    // Create legend
    const legend = L.control({ position: 'bottomright' });
    legend.onAdd = () => {
      const div = L.DomUtil.create('div', 'info legend');
      div.style.backgroundColor = 'white';
      div.style.padding = '10px';
      div.style.border = '2px solid rgba(0,0,0,0.2)';
      div.style.borderRadius = '4px';
      div.innerHTML = Object.values(layers)
        .filter(layer => layer.visible)
        .map(layer => `
          <div style="margin-bottom: 5px;">
            ${layer.icon === 'circle' 
              ? `<span style="color: ${layer.color};">●</span>`
              : layer.icon === 'blade'
              ? `<span style="color: ${layer.color};">●</span>`
              : `<i class="fas fa-${layer.icon}" style="color: ${layer.color};"></i>`
            } ${layer.name}
          </div>
        `).join('') + `
        <div style="margin-top: 5px;">
          <span style="color: #999;">---</span> Supply Lines (≤ 500km)
        </div>
      `;
      return div;
    };
    legend.addTo(mapInstanceRef.current);
    mapInstanceRef.current.legend = legend;

    setMapInitialized(true);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch data for all layers
      const layerPromises = Object.entries(layers).map(([key, layer]) => layer.fetchData(showActiveOnly));
      const responses = await Promise.all(layerPromises);

      if (responses.some(response => !response.data)) {
        throw new Error('Failed to fetch data');
      }

      // Update layer data
      const newLayerData = {};
      Object.entries(layers).forEach(([key, layer], index) => {
        newLayerData[layer.id] = responses[index].data;
      });

      setLayerData(newLayerData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading map data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const toggleLayer = (layerId) => {
    console.log('Toggling layer:', layerId);
    setLayers(prev => {
      const newLayers = {
        ...prev,
        [layerId]: {
          ...prev[layerId],
          visible: !prev[layerId].visible
        }
      };
      console.log('New layers state:', newLayers);
      return newLayers;
    });
  };

  const updateMap = () => {
    if (!mapInstanceRef.current || !mapInitialized) return;
    
    console.log('Updating map with layers:', layers);

    // Clear existing markers and lines
    mapInstanceRef.current.eachLayer((layer) => {
      if (layer instanceof L.Marker || layer instanceof L.Polyline || layer instanceof L.CircleMarker) {
        layer.remove();
      }
    });

    // Update legend
    if (mapInstanceRef.current.legend) {
      mapInstanceRef.current.legend.remove();
    }
    
    const legend = L.control({ position: 'bottomright' });
    legend.onAdd = () => {
      const div = L.DomUtil.create('div', 'info legend');
      div.style.backgroundColor = 'white';
      div.style.padding = '10px';
      div.style.border = '2px solid rgba(0,0,0,0.2)';
      div.style.borderRadius = '4px';
      div.innerHTML = Object.values(layers)
        .filter(layer => layer.visible)
        .map(layer => `
          <div style="margin-bottom: 5px;">
            ${layer.icon === 'circle' 
              ? `<span style="color: ${layer.color};">●</span>`
              : layer.icon === 'blade'
              ? `<span style="color: ${layer.color};">●</span>`
              : `<i class="fas fa-${layer.icon}" style="color: ${layer.color};"></i>`
            } ${layer.name}
          </div>
        `).join('') + `
        <div style="margin-top: 5px;">
          <span style="color: #999;">---</span> Supply Lines (≤ 500km)
        </div>
      `;
      return div;
    };
    legend.addTo(mapInstanceRef.current);
    mapInstanceRef.current.legend = legend;

    // Render markers for each visible layer
    Object.entries(layers).forEach(([key, layer]) => {
      if (layer.visible && layerData[layer.id]) {
        const data = layerData[layer.id];
        data.forEach(item => {
          const marker = layer.renderMarker(item, mapInstanceRef.current);
          if (marker) {
            marker.addTo(mapInstanceRef.current);
          }
        });
      }
    });

    // Create a layer group for lines
    if (linesLayerRef.current) {
      linesLayerRef.current.clearLayers();
    } else {
      linesLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);
    }

    // Draw supply lines
    if (layers.suppliers.visible && layerData['suppliers']) {
      const suppliers = layerData['suppliers'];
      suppliers.forEach(supplier => {
        if (supplier.latitude && supplier.longitude) {
          const drawLines = (points) => {
            points.forEach(point => {
              if (point.latitude && point.longitude) {
                const distance = L.latLng([supplier.latitude, supplier.longitude])
                  .distanceTo([point.latitude, point.longitude]) / 1000;

                if (distance < 500) {
                  const line = L.polyline(
                    [
                      [supplier.latitude, supplier.longitude],
                      [point.latitude, point.longitude]
                    ],
                    {
                      color: '#999',
                      weight: 2,
                      opacity: 0.7,
                      dashArray: '5, 10'
                    }
                  );
                  
                  line.bindTooltip(`Distance: ${Math.round(distance)}km`, {
                    permanent: false,
                    direction: 'center'
                  });
                  
                  linesLayerRef.current.addLayer(line);
                }
              }
            });
          };

          if (layers.stores.visible && layerData['stores']) {
            drawLines(layerData['stores']);
          }

          if (layers.warehouses.visible && layerData['warehouses']) {
            drawLines(layerData['warehouses'].map(w => ({
              latitude: w.location.latitude,
              longitude: w.location.longitude
            })));
          }
        }
      });
    }
  };

  // Initialize map on mount
  useEffect(() => {
    initializeMap();
    
    // Add global function for navigation
    window.navigateToWarehouseInventory = (warehouseId) => {
      navigate(`/inventory/warehouse/${warehouseId}`);
    };
    
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      // Clean up global function
      delete window.navigateToWarehouseInventory;
    };
  }, [navigate]);

  // Fetch data when map is initialized
  useEffect(() => {
    if (mapInitialized) {
      fetchData();
    }
  }, [mapInitialized]);

  // Update map when layer data or visibility changes
  useEffect(() => {
    if (mapInitialized && Object.keys(layerData).length > 0) {
      updateMap();
    }
  }, [layerData, layers, mapInitialized]);

  // Handle active only toggle
  useEffect(() => {
    if (mapInitialized) {
      fetchData();
    }
  }, [showActiveOnly]);

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">
            Network Map
          </Typography>
          <Stack direction="row" spacing={2}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={showActiveOnly}
                    onChange={(e) => setShowActiveOnly(e.target.checked)}
                    name="activeOnly"
                  />
                }
                label="Show Active Only"
              />
            </FormGroup>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={layers.stores?.visible ?? false}
                    onChange={() => toggleLayer('stores')}
                    name="showStores"
                  />
                }
                label={LAYER_LABELS.STORES}
              />
            </FormGroup>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={layers.warehouses?.visible ?? false}
                    onChange={() => toggleLayer('warehouses')}
                    name="showWarehouses"
                  />
                }
                label={LAYER_LABELS.WAREHOUSES}
              />
            </FormGroup>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={layers.suppliers?.visible ?? false}
                    onChange={() => toggleLayer('suppliers')}
                    name="showSuppliers"
                  />
                }
                label={LAYER_LABELS.SUPPLIERS}
              />
            </FormGroup>
          </Stack>
        </Box>
        <Box
          ref={mapRef}
          sx={{
            height: '600px',
            width: '100%',
            '& .leaflet-container': {
              height: '100%',
              width: '100%'
            }
          }}
        />
      </Paper>
    </Box>
  );
};

export default NetworkMap; 